import { listOrganizationCurriculum } from "@/lib/api/organizationCurriculum";
import { listTemplates } from "@/lib/api/bundleTemplates";
import { listVideos } from "@/lib/api/videos";
import { getEmployeeLinkedToUser } from "@/lib/api/employees";
import {
  fetchAssessmentSessionsForOrg,
  fetchAssessmentSessionsMergedForRespondentAndOrg,
  type AssessmentSessionRow,
} from "@/lib/api/assessments";
import { resolveActiveCurriculumForEmployee, type ActiveCurriculumForEmployee } from "@/lib/employeeCurriculum";
import type {
  BundleTemplateRow,
  EmployeeRow,
  OrganizationCurriculumRow,
  VideoRow,
} from "@/types/database";

const EMPTY_CURRICULUM: ActiveCurriculumForEmployee = {
  curriculum: null,
  introHeroPhase: null,
  featuredPlayer: null,
  showMapsPlaylistThumbnails: false,
  bundle: null,
  educationVideo: null,
  introVideo: null,
  outroVideo: null,
  orientation: null,
  results: null,
  allWithStatus: [],
  hasOrganizationAssignments: false,
};

const LINKED_TTL_MS = 5 * 60_000;
const CURRICULUM_TTL_MS = 3 * 60_000;
const SESSIONS_TTL_MS = 2 * 60_000;
const SELF_CARE_LIBRARY_TTL_MS = 5 * 60_000;

export type SelfCareLibraryData = {
  rows: OrganizationCurriculumRow[];
  videos: VideoRow[];
  templates: BundleTemplateRow[];
};

type CacheSlot<T> = {
  data: T;
  fetchedAt: number;
  inflight?: Promise<T>;
};

const linkedByKey = new Map<string, CacheSlot<EmployeeRow | null>>();
const curriculumByKey = new Map<string, CacheSlot<ActiveCurriculumForEmployee>>();
const sessionsByKey = new Map<string, CacheSlot<AssessmentSessionRow[]>>();
const selfCareLibraryByOrg = new Map<string, CacheSlot<SelfCareLibraryData>>();

function linkedKey(orgId: string, userId: string): string {
  return `linked:${orgId}:${userId}`;
}

function curriculumKey(orgId: string, jobCategoryId: string | null): string {
  return `curriculum:${orgId}:${jobCategoryId ?? "none"}`;
}

export function buildAssessmentSessionsCacheKey(
  orgId: string,
  employeeId: string,
  respondentEmail: string,
): string {
  return `sessions:${orgId}:${employeeId}:${respondentEmail}`;
}

function sessionsKey(orgId: string, employeeId: string, respondentEmail: string): string {
  return buildAssessmentSessionsCacheKey(orgId, employeeId, respondentEmail);
}

function selfCareLibraryKey(orgId: string): string {
  return `selfCare:${orgId}`;
}

function isFresh<T>(slot: CacheSlot<T> | undefined, ttlMs: number): slot is CacheSlot<T> {
  return Boolean(slot && Date.now() - slot.fetchedAt < ttlMs);
}

export function peekLinkedEmployee(orgId: string, userId: string): EmployeeRow | null | undefined {
  const slot = linkedByKey.get(linkedKey(orgId, userId));
  if (!slot) return undefined;
  return slot.data;
}

export function peekActiveCurriculum(
  orgId: string,
  jobCategoryId: string | null,
): ActiveCurriculumForEmployee | undefined {
  const slot = curriculumByKey.get(curriculumKey(orgId, jobCategoryId));
  if (!slot) return undefined;
  return slot.data;
}

export function invalidateLinkedEmployee(orgId: string, userId: string): void {
  linkedByKey.delete(linkedKey(orgId, userId));
}

export function invalidateActiveCurriculum(orgId: string, jobCategoryId: string | null): void {
  curriculumByKey.delete(curriculumKey(orgId, jobCategoryId));
}

export function peekAssessmentSessions(cacheKey: string): AssessmentSessionRow[] | undefined {
  const slot = sessionsByKey.get(cacheKey);
  if (!slot) return undefined;
  return slot.data;
}

export function invalidateAssessmentSessions(cacheKey: string): void {
  sessionsByKey.delete(cacheKey);
}

export function peekSelfCareLibrary(orgId: string): SelfCareLibraryData | undefined {
  const slot = selfCareLibraryByOrg.get(selfCareLibraryKey(orgId));
  if (!slot) return undefined;
  return slot.data;
}

export function invalidateSelfCareLibrary(orgId: string): void {
  selfCareLibraryByOrg.delete(selfCareLibraryKey(orgId));
}

export async function fetchLinkedEmployeeCached(
  orgId: string,
  userId: string,
  options?: { force?: boolean },
): Promise<EmployeeRow | null> {
  const key = linkedKey(orgId, userId);
  const existing = linkedByKey.get(key);
  if (!options?.force && isFresh(existing, LINKED_TTL_MS)) return existing.data;
  if (existing?.inflight) return existing.inflight;

  const inflight = getEmployeeLinkedToUser(orgId, userId)
    .then((row) => {
      const data = row ?? null;
      linkedByKey.set(key, { data, fetchedAt: Date.now() });
      return data;
    })
    .catch((e) => {
      linkedByKey.delete(key);
      throw e;
    });

  linkedByKey.set(key, {
    data: existing?.data ?? null,
    fetchedAt: existing?.fetchedAt ?? 0,
    inflight,
  });

  try {
    return await inflight;
  } finally {
    const slot = linkedByKey.get(key);
    if (slot) slot.inflight = undefined;
  }
}

export async function fetchActiveCurriculumCached(
  orgId: string,
  jobCategoryId: string | null,
  options?: { force?: boolean },
): Promise<ActiveCurriculumForEmployee> {
  const key = curriculumKey(orgId, jobCategoryId);
  const existing = curriculumByKey.get(key);
  if (!options?.force && isFresh(existing, CURRICULUM_TTL_MS)) return existing.data;
  if (existing?.inflight) return existing.inflight;

  const inflight = resolveActiveCurriculumForEmployee(orgId, jobCategoryId)
    .then((data) => {
      curriculumByKey.set(key, { data, fetchedAt: Date.now() });
      return data;
    })
    .catch((e) => {
      curriculumByKey.delete(key);
      throw e;
    });

  curriculumByKey.set(key, {
    data: existing?.data ?? EMPTY_CURRICULUM,
    fetchedAt: existing?.fetchedAt ?? 0,
    inflight,
  });

  try {
    return await inflight;
  } finally {
    const slot = curriculumByKey.get(key);
    if (slot) slot.inflight = undefined;
  }
}

export async function fetchAssessmentSessionsCached(
  orgId: string,
  employeeId: string,
  respondentEmail: string,
  options?: { force?: boolean },
): Promise<AssessmentSessionRow[]> {
  const key = sessionsKey(orgId, employeeId, respondentEmail);
  const existing = sessionsByKey.get(key);
  if (!options?.force && isFresh(existing, SESSIONS_TTL_MS)) return existing.data;
  if (existing?.inflight) return existing.inflight;

  const inflight = (respondentEmail.trim()
    ? fetchAssessmentSessionsMergedForRespondentAndOrg(orgId, respondentEmail)
    : fetchAssessmentSessionsForOrg(orgId)
  )
    .then((data) => {
      sessionsByKey.set(key, { data, fetchedAt: Date.now() });
      return data;
    })
    .catch((e) => {
      sessionsByKey.delete(key);
      throw e;
    });

  sessionsByKey.set(key, {
    data: existing?.data ?? [],
    fetchedAt: existing?.fetchedAt ?? 0,
    inflight,
  });

  try {
    return await inflight;
  } finally {
    const slot = sessionsByKey.get(key);
    if (slot) slot.inflight = undefined;
  }
}

export async function fetchSelfCareLibraryCached(
  organizationId: string,
  options?: { force?: boolean },
): Promise<SelfCareLibraryData> {
  const key = selfCareLibraryKey(organizationId);
  const existing = selfCareLibraryByOrg.get(key);
  if (!options?.force && isFresh(existing, SELF_CARE_LIBRARY_TTL_MS)) return existing.data;
  if (existing?.inflight) return existing.inflight;

  const inflight = Promise.all([
    listOrganizationCurriculum(organizationId),
    listVideos({
      matching_organization_catalog_including_shared: organizationId,
      require_cloudflare_stream_id: true,
    }),
    listTemplates({ is_active: true }),
  ])
    .then(([rows, videos, templates]) => {
      const data: SelfCareLibraryData = { rows, videos, templates };
      selfCareLibraryByOrg.set(key, { data, fetchedAt: Date.now() });
      return data;
    })
    .catch((e) => {
      selfCareLibraryByOrg.delete(key);
      throw e;
    });

  selfCareLibraryByOrg.set(key, {
    data: existing?.data ?? { rows: [], videos: [], templates: [] },
    fetchedAt: existing?.fetchedAt ?? 0,
    inflight,
  });

  try {
    return await inflight;
  } finally {
    const slot = selfCareLibraryByOrg.get(key);
    if (slot) slot.inflight = undefined;
  }
}
